<template>
  <div class="min-h-screen bg-zinc-950 p-8 text-zinc-100">
    <div class="max-w-6xl mx-auto space-y-8">
      
      <!-- Header -->
      <header class="flex justify-between items-center bg-zinc-900/50 backdrop-blur-md p-6 rounded-3xl shadow-sm border border-zinc-800/50 relative overflow-hidden">
        <div class="relative z-10 w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <router-link :to="{ name: 'music_analytics' }" class="inline-flex items-center gap-2 text-zinc-400 hover:text-green-400 transition-colors mb-2 text-sm font-medium">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
              Retour aux statistiques
            </router-link>
            <h1 class="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600">Tous les Artistes</h1>
            <p class="text-zinc-400 mt-1">{{ filteredArtists.length }} artistes trouvés</p>
          </div>
          
          <div class="w-full md:w-auto relative">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Rechercher un artiste..." 
              class="w-full md:w-80 bg-zinc-800/80 border border-zinc-700/50 rounded-xl py-2.5 pl-10 pr-4 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 transition-all"
            >
            <svg class="w-5 h-5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </div>
        </div>
        
        <!-- Decorative blob -->
        <div class="absolute -right-10 -top-10 w-48 h-48 bg-green-500/10 rounded-full blur-3xl"></div>
      </header>

      <div v-if="loading" class="text-center py-20">
        <div class="w-16 h-16 border-4 border-green-500/20 border-t-green-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-zinc-500 animate-pulse">Chargement de vos artistes...</p>
      </div>

      <div v-else-if="error" class="bg-red-500/10 border border-red-500/20 p-8 rounded-3xl text-center">
        <p class="text-red-400">{{ error }}</p>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        
        <router-link 
          v-for="(artist, index) in filteredArtists" 
          :key="artist.spotify_id"
          :to="{ name: 'music_artist_detail', params: { id: artist.spotify_id } }"
          class="group flex items-center gap-4 p-4 rounded-2xl bg-zinc-900/40 backdrop-blur-sm border border-zinc-800/50 hover:bg-zinc-800/50 transition-all duration-300 relative overflow-hidden block cursor-pointer hover:scale-[1.02]"
        >
          <!-- Rank -->
          <div class="w-8 text-center font-black text-lg text-zinc-600 group-hover:text-green-500/50 transition-colors">
            #{{ index + 1 }}
          </div>
          
          <!-- Image -->
          <div class="w-14 h-14 rounded-full overflow-hidden bg-zinc-800 shadow-md ring-2 ring-transparent group-hover:ring-green-500/30 transition-all">
            <img v-if="artist.images && artist.images.length > 0" :src="artist.images[artist.images.length - 1].url" class="w-full h-full object-cover" />
            <div v-else class="w-full h-full flex items-center justify-center text-zinc-600">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
            </div>
          </div>
          
          <!-- Info -->
          <div class="flex-1 min-w-0 z-10">
            <h3 class="font-bold text-base text-zinc-200 truncate group-hover:text-green-400 transition-colors">{{ artist.name }}</h3>
            <p class="text-xs text-zinc-500">{{ artist.track_count }} titres likés</p>
          </div>

          <!-- Progress Bar Background -->
          <div class="absolute bottom-0 left-0 h-1 bg-green-500/20 transition-all duration-1000 ease-out z-0 group-hover:bg-green-500/40" :style="{ width: `${(artist.track_count / maxArtistCount) * 100}%` }"></div>
        </router-link>

        <div v-if="filteredArtists.length === 0" class="col-span-full p-8 text-center text-zinc-500">
          Aucun artiste trouvé correspondant à votre recherche.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useToast } from '../../composables/useToast'

const { error: showError } = useToast()
const loading = ref(true)
const error = ref('')
const searchQuery = ref('')

interface Artist {
  spotify_id: string
  name: string
  images: Array<{url: string, height: number, width: number}>
  track_count: number
}

const allArtists = ref<Artist[]>([])

const maxArtistCount = computed(() => {
  if (allArtists.value.length === 0) return 1
  return Math.max(...allArtists.value.map(a => a.track_count))
})

const filteredArtists = computed(() => {
  if (!searchQuery.value) return allArtists.value
  const query = searchQuery.value.toLowerCase()
  return allArtists.value.filter(a => a.name.toLowerCase().includes(query))
})

const fetchArtists = async () => {
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/music/analytics/artists', {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    if (!response.ok) throw new Error('Erreur réseau')
    
    allArtists.value = await response.json()
  } catch (e) {
    console.error(e)
    error.value = "Impossible de charger la liste des artistes."
    showError(error.value)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchArtists()
})
</script>
